import { Card, Icon, pxToRem, Text } from '@lbcamden/lib-react';
import { getRiskFactorDisplayValues } from '@lbcamden/shared-utils';
import { FunctionComponent, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { FullPcfraPerson } from '~/common/types/people';

import { GeneralInfo } from './GeneralInfo';
import { HeadingInfo } from './HeadingInfo';
import { scrollCardIntoView } from './helpers';
import { PcfraActionHandlers, PcfraInfo } from './PcfraInfo';
import {
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
  ContainerDivider,
  HeadingContainer,
  ResidentInfo,
  TopContainer,
} from './ResidentCard.styled';
import { ResidentHistory } from './ResidentHistory/ResidentHistory';
import { RiskFactors } from './RiskFactors';

type Props = {
  initOpen?: boolean;
  person: FullPcfraPerson;
  riskFactorsInfo: RiskFactorsInfo;
  isPcfraManager?: boolean;
  pcfraActionHandlers: PcfraActionHandlers;
  onClickLogActivity(): void;
};

type RiskFactorsInfo = {
  floorLevel?: string;
  livesAlone: boolean;
};

export const ResidentCard: FunctionComponent<Props> = ({
  initOpen = false,
  person,
  riskFactorsInfo: { floorLevel, livesAlone },
  isPcfraManager,
  pcfraActionHandlers,
  onClickLogActivity,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(initOpen);

  const riskFactors = useMemo(
    () =>
      getRiskFactorDisplayValues({
        ...person,
        floorLevel,
        livesAlone,
      }),
    [person, floorLevel, livesAlone],
  );

  useLayoutEffect(() => {
    if (initOpen && elementRef.current) {
      scrollCardIntoView(elementRef.current);
    }
  }, []);

  return (
    <Card ref={elementRef} data-testid="resident-card">
      <CollapsibleRoot open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger data-testid="resident-card-trigger">
          <HeadingContainer>
            <Text variant="h3">{person.fullName}</Text>
            {!open && <HeadingInfo person={person} riskFactorCount={riskFactors.length} />}
          </HeadingContainer>
          <Icon icon="ui/chevron" width={pxToRem(15)} $rotate180={open} />
        </CollapsibleTrigger>

        <CollapsibleContent data-testid="resident-card-content">
          <TopContainer>
            <ResidentInfo>
              <GeneralInfo person={person} />
              <RiskFactors riskFactors={riskFactors} />
            </ResidentInfo>
            <ContainerDivider />
            <PcfraInfo
              person={person}
              isPcfraManager={isPcfraManager}
              actionHandlers={pcfraActionHandlers}
            />
          </TopContainer>
          <ResidentHistory person={person} onClickLogActivity={onClickLogActivity} />
        </CollapsibleContent>
      </CollapsibleRoot>
    </Card>
  );
};
